import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { AlertTriangle, Clock } from "lucide-react";
import { fmtINR } from "@/lib/format";

/**
 * Advance Drift Watchdog — Feb 2026 · Iter-90.
 * Dashboard tile listing vendors whose PO advances have sat un-adjusted
 * for more than 60 days. Backend: GET /api/ops/po/advance-drift?days=60.
 */
export default function AdvanceDriftWidget() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(60);

  useEffect(() => {
    api.get(`/ops/po/advance-drift?days=${days}`)
      .then((r) => setData(r.data))
      .catch(() => setData(null));
  }, [days]);

  if (!data || !data.vendors?.length) return null;

  return (
    <Card className="rounded-sm border border-amber-500/30 p-4 md:p-5" data-testid="advance-drift-widget">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-amber-600" />
          <div className="text-[10px] font-mono uppercase tracking-widest text-amber-700">
            // Advance drift watchdog
          </div>
        </div>
        <div className="flex items-center gap-2 text-[10px] font-mono text-muted-foreground">
          <span>Threshold</span>
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="rounded-sm border bg-background px-1.5 py-0.5"
            data-testid="advance-drift-days"
          >
            <option value={30}>30 days</option>
            <option value={60}>60 days</option>
            <option value={90}>90 days</option>
          </select>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
        <div className="rounded-sm border border-amber-500/20 bg-amber-500/10 p-3">
          <div className="text-[10px] font-mono uppercase text-amber-700">Vendors flagged</div>
          <div className="text-2xl font-bold tabular-nums text-amber-800 mt-1">{data.vendors.length}</div>
        </div>
        <div className="rounded-sm border border-red-500/20 bg-red-500/10 p-3">
          <div className="text-[10px] font-mono uppercase text-red-700">₹ stuck</div>
          <div className="text-2xl font-bold tabular-nums text-red-800 mt-1">{fmtINR(data.total_outstanding)}</div>
        </div>
        <div className="rounded-sm border border-slate-500/20 bg-slate-500/5 p-3">
          <div className="text-[10px] font-mono uppercase text-slate-700">Advances</div>
          <div className="text-2xl font-bold tabular-nums text-slate-800 mt-1">{data.total_advance_count}</div>
        </div>
        <div className="rounded-sm border border-slate-500/20 bg-slate-500/5 p-3">
          <div className="text-[10px] font-mono uppercase text-slate-700">Threshold</div>
          <div className="text-2xl font-bold tabular-nums text-slate-800 mt-1">&gt;{data.threshold_days}d</div>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-[10px] font-mono uppercase text-muted-foreground bg-muted/40">
            <tr>
              <th className="p-2 text-left">Vendor</th>
              <th className="p-2 text-left">Open POs</th>
              <th className="p-2 text-right">Advances</th>
              <th className="p-2 text-right">Outstanding</th>
              <th className="p-2 text-right">Oldest</th>
              <th className="p-2"></th>
            </tr>
          </thead>
          <tbody>
            {data.vendors.slice(0, 6).map((v) => (
              <tr key={v.vendor_id} className="border-t" data-testid={`advance-drift-row-${v.vendor_id}`}>
                <td className="p-2 font-medium">{v.vendor_name}</td>
                <td className="p-2 font-mono text-[11px] text-muted-foreground">{v.pos.join(", ") || "—"}</td>
                <td className="p-2 text-right font-mono tabular-nums">{v.advance_count}</td>
                <td className="p-2 text-right font-mono tabular-nums font-semibold text-red-700">{fmtINR(v.outstanding)}</td>
                <td className="p-2 text-right font-mono tabular-nums text-amber-700">
                  <Clock className="w-3 h-3 inline mr-1" />{v.oldest_days}d
                </td>
                <td className="p-2 text-right">
                  <Link
                    to={`/app/vendors?vendor=${v.vendor_id}`}
                    className="text-[11px] font-mono uppercase text-accent hover:underline"
                    data-testid={`advance-drift-open-${v.vendor_id}`}
                  >
                    Open →
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {data.vendors.length > 6 && (
        <div className="mt-2 text-[11px] text-muted-foreground text-right">
          + {data.vendors.length - 6} more vendors flagged
        </div>
      )}
    </Card>
  );
}
